<template>
  <div class="edit-user-wrapper">
    <p class="title">{{ $t('app.panels.editUser.title') }}</p>
    <div class="separator"></div>
    <p class="subtitle">{{ $t('app.panels.editUser.subtitle') }}</p>
    <LabeledSelect
      id="role"
      name="role"
      :label="$t('app.panels.editUser.roleSelect.label')"
      class="sm"
      :model-value="selectedRole"
      :searchable="false"
      :clearable="false"
      :disabled="!canEditRole"
      :options="filteredOrganisationRoles"
      @update:modelValue="updateSelectedRole"
    >
      <template #selected-option="option">
        <div class="xs-text">
          {{ $t(`app.company.members.${option.name}`) }}
        </div>
      </template>
      <template #option="option">
        <div class="xs-text">
          {{ $t(`app.company.members.${option.name}`) }}
        </div>
      </template>
    </LabeledSelect>
    <div class="separator"></div>
    <ScopeSelector :scopes="selectedScopes" :role="selectedRole" @selected-scopes="selectedScopes = $event"></ScopeSelector>
    <div class="button-group">
      <Button class="sm green" :label="$t('other.actions.confirm')" @click="updateRoleAndGrants"></Button>
      <Button class="sm secondary" :label="$t('other.actions.cancel')" @click="emit('close')"></Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useToast } from 'vue-toastification';

import { useAllOrgnisationRoles } from '@/queries/organisation.query';
import { useRoles, useUser } from '@/queries/user.query';
import { useMemberGrants, useUpdateMemberGrants } from '@/queries/members.query';

import { MemberDetails, ScopeRef, ScopeRefKind } from '@/types/member';
import { AppRole, MemberRole } from '@/types/role';

import Button from '@/components/global/Button.vue';
import ScopeSelector from './ScopeSelector.vue';
import LabeledSelect from '@/components/global/LabeledSelect.vue';

const emit =  defineEmits<{
  (e: 'close'): void,
}>()

const props = defineProps<{
  member?: MemberDetails,
}>();

const memberId = computed(() => {
  return props?.member?.member?.id;
});

const { t } = useI18n();
const toast = useToast();
const { data: user, isPending: isUserPending } = useUser();
const { data: currentUserRoles, isPending: isCurrentUserRolesPending } = useRoles();
const { data: organisationRoles, isPending: isOrganisationRolesPending } = useAllOrgnisationRoles();
const { data: memberGrants, isPending: isGrantsPending } = useMemberGrants(memberId);
const { mutate: updateMemberGrants } = useUpdateMemberGrants();

const selectedScopes = ref<ScopeRef[]>([]);
const selectedRole = ref<AppRole>();

watch(memberGrants, () => {
  if (isGrantsPending.value) return;
  selectedRole.value = memberGrants?.value[0]?.role;

  selectedScopes.value = memberGrants.value.map((g) => g.scope);
}, { deep: true });

onMounted(() => {
  if (isGrantsPending.value) return;
  selectedRole.value = memberGrants?.value[0]?.role;
  selectedScopes.value = memberGrants.value?.map((g) => g.scope);
});

const canEditRole = computed(() => {
  if (user.value?.id === props?.member?.account.id) return false;
  if (memberAppRole.value?.rank <= currentUserRoles.value?.[0]?.rank) return false;
  return true;
});

const memberAppRole = computed(() => {
  if (!organisationRoles.value || isOrganisationRolesPending.value || !props?.member?.roles_ids[0]) return undefined;
  const memberRole = organisationRoles.value.find((role) => role.id === props.member.roles_ids[0]);
  return memberRole;
});

const filteredOrganisationRoles = computed(() => {
  if (isOrganisationRolesPending.value) return [];
  return organisationRoles.value.filter((role) => {
    if (role.role_backward_compatibility_id === MemberRole.PARTCIPANT) return false;
    return true;
  })
});

function updateRoleAndGrants() {
  updateMemberGrants({
    memberId: memberId.value,
    roleId: selectedRole.value.id,
    scopes: selectedScopes.value,
  }, {
    onSuccess: () => {
      toast.success(t('app.toasts.success'));
    },
    onError: () => {
      toast.error(t('other.error.ERROR_UNKNOWN'));
    }
  });

  emit('close');
}

function updateSelectedRole(role: AppRole) {
  if (selectedRole.value.id !== role.id) selectedScopes.value = [];
  selectedRole.value = role;
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.edit-user-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;

  height: 100%;
  width: 100%;

  .title {
    font-size: 20px;
    font-weight: 700;
  }

  .subtitle {
    font-size: 14px;
    font-weight: 600;
  }

  .label {
    font-size: 12px;
    color: var(--label-color);
    margin-bottom: 6px;
  }

  .separator {
    width: 100%;
    height: 1px;
    background-color: var(--light-grey-color);
  }

  #role {
    width: 50%;
  }

  .button-group {
    display: flex;
    gap: 8px;
  }

}
</style>